import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, Loader2, MapPin, Target, Zap, TrendingUp, ArrowLeft, Home } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimpleWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const existingClientId = searchParams.get('clientId');
  
  const [currentStep, setCurrentStep] = useState(existingClientId ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState(existingClientId);
  const [brandId, setBrandId] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [locations, setLocations] = useState(null);
  const [existingClient, setExistingClient] = useState(null);
  const [cityInput, setCityInput] = useState("");  // Input temporário para adicionar cidade
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    product: "",
    price: "",
    audience: "",
    city: "",
    cities: [],  // Array de múltiplas cidades
    region: "",  // Nome da região (opcional)
    goal: ""
  });

  // Funções para gerenciar múltiplas cidades
  const addCity = () => {
    const city = cityInput.trim();
    if (city && !formData.cities.includes(city)) {
      setFormData(prev => ({
        ...prev,
        cities: [...prev.cities, city],
        city: prev.cities.length === 0 ? city : prev.city  // Primeira cidade é a principal
      }));
      setCityInput("");
    }
  };

  const removeCity = (cityToRemove) => {
    setFormData(prev => {
      const newCities = prev.cities.filter(c => c !== cityToRemove);
      return {
        ...prev,
        cities: newCities,
        city: newCities[0] || ""  // Atualizar cidade principal
      };
    });
  };

  // Carregar dados do cliente existente
  useEffect(() => {
    if (existingClientId) {
      loadExistingClient(existingClientId);
    }
  }, [existingClientId]);

  const loadExistingClient = async (id) => {
    try {
      const res = await axios.get(`${API}/clients/${id}`);
      setExistingClient(res.data);
      setFormData(prev => ({
        ...prev,
        name: res.data.name,
        email: res.data.email
      }));
      toast.info(`Criando nova marca para ${res.data.name}`);
    } catch (error) {
      console.error("Erro ao carregar cliente:", error);
    }
  };

  // ETAPAS DO WIZARD
  const steps = existingClientId ? [
    { id: 2, title: "Seu Negócio", icon: Zap, description: "O que você vende?" },
    { id: 3, title: "Análise IA", icon: TrendingUp, description: "Entendendo seu público" },
    { id: 4, title: "Locais Certos", icon: MapPin, description: "Onde anunciar?" }
  ] : [
    { id: 1, title: "Seus Dados", icon: Target, description: "Quem é você?" },
    { id: 2, title: "Seu Negócio", icon: Zap, description: "O que você vende?" },
    { id: 3, title: "Análise IA", icon: TrendingUp, description: "Entendendo seu público" },
    { id: 4, title: "Locais Certos", icon: MapPin, description: "Onde anunciar?" }
  ];

  // ETAPA 1: DADOS PESSOAIS
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-red-600 mb-2">👋 Olá! Vamos começar?</h2>
        <p className="text-gray-600">Primeiro, me diga quem é você</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="name" className="text-lg">Seu nome</Label>
          <Input
            id="name"
            placeholder="Ex: João Silva"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="mt-2 h-12 text-lg"
          />
        </div>

        <div>
          <Label htmlFor="email" className="text-lg">Seu email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Ex: joao@email.com"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="mt-2 h-12 text-lg"
          />
        </div>
      </div>

      <Button 
        onClick={() => setCurrentStep(2)} 
        className="w-full h-14 text-lg bg-red-600 hover:bg-red-700"
        disabled={!formData.name || !formData.email}
      >
        Continuar →
      </Button>
    </div>
  );

  // ETAPA 2: NEGÓCIO
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-red-600 mb-2">🏪 Seu Negócio</h2>
        <p className="text-gray-600">Me conta o que você vende</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="product" className="text-lg">O que você vende?</Label>
          <Textarea
            id="product"
            placeholder="Ex: Roupas femininas populares, acessíveis, do dia a dia"
            value={formData.product}
            onChange={(e) => setFormData({...formData, product: e.target.value})}
            className="mt-2 min-h-[100px] text-base"
          />
          <p className="text-sm text-gray-500 mt-1">💡 Seja específico! Isso ajuda muito</p>
        </div>

        <div>
          <Label htmlFor="price" className="text-lg">Quanto custa?</Label>
          <Input
            id="price"
            placeholder="Ex: R$ 20 a R$ 50 por peça"
            value={formData.price}
            onChange={(e) => setFormData({...formData, price: e.target.value})}
            className="mt-2 h-12 text-lg"
          />
        </div>

        <div>
          <Label htmlFor="audience" className="text-lg">Quem compra de você?</Label>
          <Textarea
            id="audience"
            placeholder="Ex: Mulheres de 25-45 anos, classe média, que trabalham fora"
            value={formData.audience}
            onChange={(e) => setFormData({...formData, audience: e.target.value})}
            className="mt-2 min-h-[100px] text-base"
          />
        </div>

        <div>
          <Label htmlFor="city" className="text-lg">Em quais cidades/regiões?</Label>
          <div className="mt-2 space-y-2">
            {/* Lista de cidades adicionadas */}
            {formData.cities.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.cities.map((city, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary" 
                    className="px-3 py-1 text-sm flex items-center gap-2"
                  >
                    <MapPin className="h-3 w-3" />
                    {city}
                    {index === 0 && <span className="text-xs text-gray-500">(principal)</span>}
                    <button
                      type="button"
                      onClick={() => removeCity(city)}
                      className="ml-1 text-gray-500 hover:text-red-500"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Input para adicionar nova cidade */}
            <div className="flex gap-2">
              <Input
                id="city"
                placeholder="Ex: São Paulo, Guarulhos, Osasco..."
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCity())}
                className="h-12 text-lg flex-1"
              />
              <Button 
                type="button"
                onClick={addCity}
                variant="outline"
                className="h-12 px-4"
                disabled={!cityInput.trim()}
              >
                + Adicionar
              </Button>
            </div>
            <p className="text-sm text-gray-500">
              💡 Adicione várias cidades para campanhas regionais. Ex: "Grande São Paulo"
            </p>
          </div>
        </div>

        <div>
          <Label htmlFor="region" className="text-lg">Nome da região (opcional)</Label>
          <Input
            id="region"
            placeholder="Ex: Grande São Paulo, ABC Paulista, Baixada Santista..."
            value={formData.region}
            onChange={(e) => setFormData({...formData, region: e.target.value})}
            className="mt-2 h-12 text-lg"
          />
          <p className="text-sm text-gray-500 mt-1">
            Dê um nome para identificar esta área de atuação
          </p>
        </div>

        <div>
          <Label htmlFor="goal" className="text-lg">Qual seu objetivo?</Label>
          <Input
            id="goal"
            placeholder="Ex: Aumentar vendas na loja física"
            value={formData.goal}
            onChange={(e) => setFormData({...formData, goal: e.target.value})}
            className="mt-2 h-12 text-lg"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <Button 
          onClick={() => setCurrentStep(1)} 
          variant="outline"
          className="w-1/3 h-14 text-lg"
        >
          ← Voltar
        </Button>
        <Button 
          onClick={handleAnalyze} 
          className="w-2/3 h-14 text-lg bg-red-600 hover:bg-red-700"
          disabled={!formData.product || !formData.price || !formData.audience || formData.cities.length === 0 || loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Analisando...
            </>
          ) : (
            `Analisar em ${formData.cities.length} cidade(s) 🚀`
          )}
        </Button>
      </div>
    </div>
  );

  // ETAPA 3: ANÁLISE
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-red-600 mb-2">🤖 Análise Completa!</h2>
        <p className="text-gray-600">Descobri quem é seu público de verdade</p>
      </div>

      {analysis && (
        <div className="space-y-4">
          <Card className="border-2 border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-red-600" />
                Perfil do Público
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Badge variant="outline" className="mb-2">Classe Social</Badge>
                <p className="text-lg font-medium">{analysis.classe_social}</p>
              </div>
              <div>
                <Badge variant="outline" className="mb-2">Faixa de Preço</Badge>
                <p className="text-lg font-medium">{analysis.faixa_preco}</p>
              </div>
              <div>
                <Badge variant="outline" className="mb-2">Público Real</Badge>
                <p className="text-base">{analysis.publico_real}</p>
              </div>
            </CardContent>
          </Card>

          {analysis.diagnostico && (
            <Card className="border-2 border-yellow-200 bg-yellow-50">
              <CardHeader>
                <CardTitle className="text-yellow-800">💡 Descoberta Importante</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base">{analysis.diagnostico}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Button 
        onClick={handleFindLocations} 
        className="w-full h-14 text-lg bg-red-600 hover:bg-red-700"
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Buscando locais...
          </>
        ) : (
          "Encontrar Locais Perfeitos 📍"
        )}
      </Button>
    </div>
  );

  // ETAPA 4: LOCAIS
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-red-600 mb-2">📍 Locais Encontrados!</h2>
        <p className="text-gray-600">Vamos anunciar aqui quando seu cliente passar perto</p>
      </div>

      {locations && (
        <div className="space-y-4">
          <Card className="border-2 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-green-800">✅ Tudo Pronto!</CardTitle>
              <CardDescription className="text-base">
                Encontramos <strong>{locations.places?.length || 0} locais perfeitos</strong> para seu público
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {/* Resumo por região/cidades */}
                {locations.region && (
                  <p className="text-sm">
                    <strong>Região:</strong> {locations.region}
                  </p>
                )}
                {locations.cities_searched && locations.cities_searched.length > 1 && (
                  <div className="text-sm">
                    <strong>Cidades:</strong>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {locations.cities_searched.map((city, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {city} ({locations.places_by_city?.[city] || 0})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-sm">
                  <strong>Perfil:</strong> {locations.perfil_economico?.toUpperCase()}
                </p>
                <p className="text-sm">
                  <strong>Total:</strong> {locations.total_places || locations.places?.length || 0} locais encontrados
                </p>
                <p className="text-sm">
                  <strong>Cobertura:</strong> {locations.coverage_summary?.total_coverage_area_km2?.toFixed(1)} km²
                </p>
                <p className="text-sm">
                  <strong>Geofences:</strong> {locations.geofences?.length || 0} cercas virtuais criadas
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-3 max-h-[400px] overflow-y-auto">
            {locations.places?.slice(0, 15).map((place, idx) => (
              <Card key={idx} className="hover:border-red-300 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-base truncate">{place.name}</p>
                      <p className="text-sm text-gray-600 truncate">{place.address}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">{place.category}</Badge>
                        {place.search_city && (
                          <Badge variant="outline" className="text-xs">{place.search_city}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {locations.places?.length > 15 && (
            <p className="text-center text-sm text-gray-500">
              + {locations.places.length - 15} outros locais
            </p>
          )}
        </div>
      )}

      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <h3 className="font-bold text-blue-900 mb-2">🎯 O que acontece agora?</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Seu cliente passa perto desses locais em {locations?.cities_searched?.length || 1} cidade(s)</span>
          </li>
          <li className="flex gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Seu anúncio aparece no Instagram/Facebook dele</span>
          </li>
          <li className="flex gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Ele vê que você está PERTO e visita sua loja!</span>
          </li>
        </ul>
      </div>

      <div className="flex gap-3">
        <Button 
          onClick={() => navigate(`/dashboard/${clientId}`)} 
          className="flex-1 h-14 text-lg bg-green-600 hover:bg-green-700"
        >
          Ir para Dashboard 🎉
        </Button>
        <Button 
          onClick={() => navigate("/dev")} 
          variant="outline"
          className="h-14 border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-black"
        >
          Voltar ao Painel DEV
        </Button>
      </div>
    </div>
  );

  // FUNÇÕES
  const handleAnalyze = async () => {
    setLoading(true);
    try {
      let currentClientId = clientId;
      
      // 1. Criar cliente apenas se não existir
      if (!currentClientId) {
        const clientRes = await axios.post(`${API}/clients`, {
          name: formData.name,
          email: formData.email
        });
        currentClientId = clientRes.data.id;
        setClientId(currentClientId);
      }

      // 2. Criar marca com múltiplas cidades
      const brandRes = await axios.post(`${API}/brands`, {
        client_id: currentClientId,
        product_service: formData.product,
        target_audience: formData.audience,
        city: formData.cities[0] || formData.city,  // Cidade principal
        cities: formData.cities,  // Todas as cidades
        region: formData.region || formData.cities.join(", "),  // Nome da região
        avg_price: formData.price,
        goal: formData.goal,
        instagram_url: "",
        website_url: ""
      });
      setBrandId(brandRes.data.id);

      // 3. Analisar
      const analysisRes = await axios.post(`${API}/brands/${brandRes.data.id}/analyze`);
      setAnalysis(analysisRes.data.analysis);

      toast.success(`Análise concluída para ${formData.cities.length} cidade(s)!`);
      setCurrentStep(3);
    } catch (error) {
      console.error(error);
      if (error.response?.status === 402) {
        toast.error("Saldo de tokens insuficiente. Compre mais tokens para continuar.");
      } else {
        toast.error("Erro ao analisar. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFindLocations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/locations/recommend/${brandId}`);
      setLocations(res.data);
      
      // Mostrar resumo por cidade
      const citiesSearched = res.data.cities_searched?.length || 1;
      const totalPlaces = res.data.total_places || res.data.places?.length || 0;
      toast.success(`🎯 ${totalPlaces} locais em ${citiesSearched} cidade(s)!`);
      
      setCurrentStep(4);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao buscar locais.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50">
      {/* HEADER COM PROGRESSO */}
      <div className="bg-white border-b-2 border-red-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Navigation Buttons */}
          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-gray-500 hover:text-gray-900"
            >
              <Home className="w-4 h-4 mr-1" />
              Início
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Voltar
            </Button>
            {existingClientId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dev")}
                className="text-yellow-600 hover:text-yellow-700"
              >
                Painel DEV
              </Button>
            )}
          </div>

          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-red-600">Mídia Tomahawk</h1>
            <Badge variant="outline" className="text-base">
              Etapa {currentStep} de {steps.length}
            </Badge>
          </div>

          {/* STEPPER */}
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-colors ${
                    currentStep > step.id 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : currentStep === step.id
                      ? 'bg-red-600 border-red-600 text-white'
                      : 'bg-gray-200 border-gray-300 text-gray-500'
                  }`}>
                    {currentStep > step.id ? (
                      <CheckCircle2 className="h-6 w-6" />
                    ) : (
                      <step.icon className="h-6 w-6" />
                    )}
                  </div>
                  <p className={`text-xs mt-2 font-medium text-center ${
                    currentStep >= step.id ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </p>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 transition-colors ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CONTEÚDO */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="border-2 shadow-lg">
          <CardContent className="p-8">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SimpleWizard;
